import { z } from 'zod';

const VisitAttendee = z.object({
  name: z.string(),
  role: z.string().optional(),
});

const VisitRefrigerationChecks = z
  .object({
    leaksPresent: z.boolean().optional(),
    leaksReported: z.boolean().optional(),
    leakSource: z.string().optional(),
    leakResolution: z.string().optional(),
    leakInjuries: z.boolean().optional(),
    leakNotes: z.string().optional(),
    condensationPressure: z.string().optional(),
    oilTemperature: z.string().optional(),
    parametersNotes: z.string().optional(),
    ordinaryMaintenance: z.string().optional(),
    extraordinaryWork: z.boolean().optional(),
    extraordinaryDetails: z.string().optional(),
  })
  .default({});

const VisitSafetySensors = z
  .object({
    status: z.string().optional(),
    lastCalibration: z.string().optional(),
    periodicityMonths: z.number().int().min(1).optional(),
    lastElementReplacement: z.string().optional(),
    replacementSchedule: z.string().optional(),
    maintainer: z.string().optional(),
  })
  .default({});

const VisitSafetyReliefValves = z
  .object({
    setpoint: z.string().optional(),
    nextDue: z.string().optional(),
    periodYears: z.number().int().min(0).optional(),
  })
  .default({});

const VisitSafetyShutoffValves = z
  .object({
    type: z.string().optional(),
    sealedON: z.boolean().optional(),
    sealedNonON: z.boolean().optional(),
    integrity: z.string().optional(),
  })
  .default({});

const VisitSafetyExtractors = z
  .object({
    selectorState: z.string().optional(),
    testRun: z.boolean().optional(),
    activationMethod: z.string().optional(),
  })
  .default({});

const VisitSafetyScrubber = z
  .object({
    extractorSelector: z.string().optional(),
    extractorTest: z.boolean().optional(),
    pumpSelector: z.string().optional(),
    pumpTest: z.boolean().optional(),
  })
  .default({});

const VisitSafetyAbatementSystem = z
  .object({
    present: z.boolean().optional(),
    selectorState: z.string().optional(),
    testRun: z.boolean().optional(),
    activationMethod: z.string().optional(),
    trim: z.string().optional(),
    notes: z.string().optional(),
  })
  .default({});

const VisitSafetyEmergencyShutoff = z
  .object({
    button: z.string().optional(),
    detectors: z.string().optional(),
    trim: z.string().optional(),
  })
  .default({});

const VisitSafetyAlarmPanel = z
  .object({
    state: z.string().optional(),
    history: z.string().optional(),
    detectorsDisabled: z.boolean().optional(),
    otherIssues: z.boolean().optional(),
    issueDetails: z.string().optional(),
  })
  .default({});

const VisitSafetyDialer = z
  .object({
    state: z.string().optional(),
    contacts: z.string().optional(),
  })
  .default({});

const VisitSafety = z
  .object({
    sensors: VisitSafetySensors,
    reliefValves: VisitSafetyReliefValves,
    shutoffValves: VisitSafetyShutoffValves,
    extractors: VisitSafetyExtractors,
    scrubber: VisitSafetyScrubber,
    abatementSystem: VisitSafetyAbatementSystem,
    emergencyShutoff: VisitSafetyEmergencyShutoff,
    alarmPanel: VisitSafetyAlarmPanel,
    dialer: VisitSafetyDialer,
    condenserWaterConductivity: z.union([z.number(), z.string()]).optional(),
    conductivityNotes: z.string().optional(),
    alarmButton: z.string().optional(),
    audioVisualAlarm: z.string().optional(),
    exitRoutes: z.string().optional(),
    valveControls: z.string().optional(),
    notes: z.string().optional(),
  })
  .default({
    sensors: {},
    reliefValves: {},
    shutoffValves: {},
    extractors: {},
    scrubber: {},
    abatementSystem: {},
    emergencyShutoff: {},
    alarmPanel: {},
    dialer: {},
  });

export const VisitUnitCheck = z.object({
  id: z.string(),
  unitId: z.string(),
  unitName: z.string(),
  refrigeration: VisitRefrigerationChecks,
  safety: VisitSafety,
});

const VisitResult = z.enum(['conforme', 'non_conforme', 'non_verificabile']);
const VisitVerifiedBy = z.enum(['DT', 'PP', 'RP']);

export const VisitDetailedCheck = z.object({
  id: z.string(),
  unitId: z.string().optional(),
  unitName: z.string(),
  item: z.string(),
  result: VisitResult,
  verifiedBy: VisitVerifiedBy,
  notes: z.string().optional(),
});

const VisitDpiGeneral = z
  .object({
    completeness: z.string().optional(),
    accessibility: z.string().optional(),
    condition: z.string().optional(),
    keysPresent: z.boolean().optional(),
    maintainer: z.string().optional(),
    notes: z.string().optional(),
  })
  .default({});

const VisitDpiMaskK = z
  .object({
    condition: z.string().optional(),
    readiness: z.string().optional(),
  })
  .default({});

const VisitDpiFiltersK = z
  .object({
    quantity: z.number().int().min(0).optional(),
    type: z.string().optional(),
    valence: z.string().optional(),
    capacity: z.string().optional(),
    expiry: z.string().optional(),
  })
  .default({});

const VisitDpiScba = z
  .object({
    compliance: z.string().optional(),
    model: z.string().optional(),
    quantity: z.number().int().min(0).optional(),
    liters: z.number().nonnegative().optional(),
    ratedPressure: z.number().nonnegative().optional(),
    measuredPressure: z.number().nonnegative().optional(),
    regulatorType: z.string().optional(),
    inspectionPeriodMonths: z.number().int().min(1).optional(),
    lastService: z.string().optional(),
    nextCylinderInspection: z.string().optional(),
  })
  .default({});

const VisitDpiScbaMask = z
  .object({
    condition: z.string().optional(),
    readiness: z.string().optional(),
  })
  .default({});

const VisitDpiSuits = z
  .object({
    type: z.string().optional(),
    quantity: z.number().int().min(0).optional(),
    compliance: z.string().optional(),
  })
  .default({});

const VisitDpiSimpleItem = z
  .object({
    quantity: z.number().int().min(0).optional(),
    compliance: z.string().optional(),
  })
  .default({});

export const VisitDpiInventory = z
  .object({
    general: VisitDpiGeneral,
    maskK: VisitDpiMaskK,
    filtersK: VisitDpiFiltersK,
    scba: VisitDpiScba,
    scbaMask: VisitDpiScbaMask,
    suits: VisitDpiSuits,
    gloves: VisitDpiSimpleItem,
    boots: VisitDpiSimpleItem,
    firstAidKit: z.string().optional(),
    signage: z
      .object({
        state: z.string().optional(),
        notes: z.string().optional(),
      })
      .default({}),
    eyeWash: z.string().optional(),
    notes: z.string().optional(),
  })
  .default({
    general: {},
    maskK: {},
    filtersK: {},
    scba: {},
    scbaMask: {},
    suits: {},
    gloves: {},
    boots: {},
    signage: {},
  });

export const VisitInfraVerification = z
  .object({
    emergencyLightingExit: z.string().optional(),
    emergencyLightingIntervention: z.string().optional(),
    lightingNotes: z.string().optional(),
    doors: z.string().optional(),
    cctv: z.string().optional(),
    intrusion: z.string().optional(),
    dvrProcedures: z.string().optional(),
    safetySuggestions: z.string().optional(),
  })
  .default({});

const VisitEmergencyParticipant = z.object({
  name: z.string(),
  role: z.string().optional(),
  signature: z.boolean().optional(),
  notes: z.string().optional(),
});

export const VisitEmergencyDrill = z
  .object({
    date: z.string().optional(),
    responsabili: z.string().optional(),
    scope: z.string().optional(),
    conformity: z.string().optional(),
    start: z.string().optional(),
    end: z.string().optional(),
    lastFullDrill: z.string().optional(),
    frequency: z.string().optional(),
    method: z.string().optional(),
    methodNotes: z.string().optional(),
    participants: z.array(VisitEmergencyParticipant).default([]),
  })
  .default({ participants: [] });

const VisitTrainingChecklist = z
  .object({
    pericoli: z.boolean().optional(),
    procedure: z.boolean().optional(),
    approfondimenti: z.boolean().optional(),
    dvrAggiornato: z.boolean().optional(),
    dpiAggiornati: z.boolean().optional(),
  })
  .default({});

const VisitTrainingParticipant = z.object({
  name: z.string(),
  signature: z.boolean().optional(),
});

export const VisitTrainingVerification = z
  .object({
    executed: z.boolean().optional(),
    scheduledFor: z.string().optional(),
    checklist: VisitTrainingChecklist,
    participants: z.array(VisitTrainingParticipant).default([]),
  })
  .default({ checklist: {}, participants: [] });

const VisitDpiSimulationRun = z.object({
  id: z.string().optional(),
  personName: z.string(),
  prepMin: z.number().nonnegative().optional(),
  workMin: z.number().nonnegative().optional(),
  pStart: z.number().nonnegative().optional(),
  pEnd: z.number().nonnegative().optional(),
  consumo: z.number().nonnegative().optional(),
  conforme: z.boolean().optional(),
  segments: z.string().optional(),
  position: z.string().optional(),
  stairsRequired: z.boolean().optional(),
  executionMode: z.string().optional(),
  difficulties: z.string().optional(),
  suggestions: z.string().optional(),
});

export const VisitDpiIIIcatSimulation = z
  .object({
    runs: z.array(VisitDpiSimulationRun).default([]),
  })
  .default({ runs: [] });

export const VisitRegister = z.object({
  id: z.string(),
  type: z.string(),
  state: z.string().optional(),
  chargeRecorded: z.boolean().optional(),
  chargeKg: z.number().nonnegative().optional(),
  dischargeRecorded: z.boolean().optional(),
  dischargeKg: z.number().nonnegative().optional(),
  unchanged: z.boolean().optional(),
  totalKg: z.number().nonnegative().optional(),
  notes: z.string().optional(),
});

export const VisitInailCompliance = z
  .object({
    equipmentType: z.string().optional(),
    ispeslAntePed: z.boolean().optional(),
    registered: z.boolean().optional(),
    registrationNotes: z.string().optional(),
    periodicInspection: z
      .object({
        function: z.boolean().optional(),
        integrity: z.boolean().optional(),
      })
      .default({}),
    notes: z.string().optional(),
  })
  .default({ periodicInspection: {} });

const VisitScorecardEntry = z.object({
  id: z.string(),
  date: z.string(),
  disponibilitaPianificazione: z.number().int().min(0).max(10).optional(),
  disponibilitaPersonale: z.number().int().min(0).max(10).optional(),
  disponibilitaFormazione: z.number().int().min(0).max(10).optional(),
  disponibilitaProve: z.number().int().min(0).max(10).optional(),
  interesseSopralluogo: z.number().int().min(0).max(10).optional(),
  interesseFormazione: z.number().int().min(0).max(10).optional(),
  interesseConoscenze: z.number().int().min(0).max(10).optional(),
  interesseSicurezza: z.number().int().min(0).max(10).optional(),
  efficienzaOrdine: z.number().int().min(0).max(10).optional(),
  efficienzaPulizia: z.number().int().min(0).max(10).optional(),
  efficienzaRisposta: z.number().int().min(0).max(10).optional(),
  efficienzaEsecuzione: z.number().int().min(0).max(10).optional(),
  efficienzaPuntualita: z.number().int().min(0).max(10).optional(),
  documentaleAccesso: z.number().int().min(0).max(10).optional(),
  documentaleCompletezza: z.number().int().min(0).max(10).optional(),
  sicurezzaComplessiva: z.number().int().min(0).max(10).optional(),
  notes: z.string().optional(),
});

export const VisitScorecard = z
  .object({
    byDate: z.array(VisitScorecardEntry).default([]),
  })
  .default({ byDate: [] });

export const VisitData = z.object({
  attendees: z.array(VisitAttendee).default([]),
  unitChecks: z.array(VisitUnitCheck).default([]),
  detailedChecks: z.array(VisitDetailedCheck).default([]),
  dpiInventory: VisitDpiInventory.optional(),
  infraVerification: VisitInfraVerification.optional(),
  emergencyDrill: VisitEmergencyDrill.optional(),
  trainingVerification: VisitTrainingVerification.optional(),
  dpiIIIcatSimulation: VisitDpiIIIcatSimulation.optional(),
  registers: z.array(VisitRegister).default([]),
  inailCompliance: VisitInailCompliance.optional(),
  scorecard: VisitScorecard.optional(),
});

export const VisitContext = VisitData.extend({
  date: z.string().optional(),
  period: z.string().optional(),
  frequencyMonths: z.number().int().min(1).optional(),
  inspector: z.string().optional(),
});

type VisitAttendee = z.infer<typeof VisitAttendee>;
export type VisitUnitCheck = z.infer<typeof VisitUnitCheck>;
export type VisitDetailedCheck = z.infer<typeof VisitDetailedCheck>;
export type VisitDpiInventory = z.infer<typeof VisitDpiInventory>;
export type VisitInfraVerification = z.infer<typeof VisitInfraVerification>;
export type VisitEmergencyDrill = z.infer<typeof VisitEmergencyDrill>;
export type VisitTrainingVerification = z.infer<typeof VisitTrainingVerification>;
export type VisitDpiIIIcatSimulation = z.infer<typeof VisitDpiIIIcatSimulation>;
export type VisitRegister = z.infer<typeof VisitRegister>;
export type VisitInailCompliance = z.infer<typeof VisitInailCompliance>;
export type VisitScorecard = z.infer<typeof VisitScorecard>;
export type VisitData = z.infer<typeof VisitData>;
export type VisitContextType = z.infer<typeof VisitContext>;
